import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, TrendingUp, Filter, ExternalLink,
} from "lucide-react";

/* Range selector — mirrors the AdminEconomics style so the UX feels unified. */
const RANGE_OPTIONS = [
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
  { days: 365, label: "1y" },
];

/* Colors per publishable network group. Chosen to be brand-recognizable
 * (Google blue, Meta blue, TikTok pink/cyan, YouTube red, etc.) so the stacked
 * bar / chip encoding is immediately readable. */
const GROUP_COLORS = {
  "Google":            { chip: "bg-[#EBF3FE] text-[#1A73E8] border-[#1A73E8]/20", bar: "#1A73E8" },
  "Meta":              { chip: "bg-[#E7F0FF] text-[#1877F2] border-[#1877F2]/20", bar: "#1877F2" },
  "TikTok":            { chip: "bg-[#FEE5EC] text-[#EE1D52] border-[#EE1D52]/20", bar: "#EE1D52" },
  "YouTube":           { chip: "bg-[#FEE7E7] text-[#FF0000] border-[#FF0000]/20", bar: "#FF0000" },
  "Other Search":      { chip: "bg-[#FEF3E7] text-[#B45309] border-amber-200",     bar: "#F59E0B" },
  "Other Social":      { chip: "bg-[#EDE9FE] text-[#6D28D9] border-violet-200",    bar: "#8B5CF6" },
  "Nextdoor":          { chip: "bg-[#EAF7F0] text-[#00B551] border-emerald-200",   bar: "#00B551" },
  "Email":             { chip: "bg-[#F0F9FF] text-[#0369A1] border-sky-200",       bar: "#0EA5E9" },
  "SMS":               { chip: "bg-[#ECFDF5] text-[#047857] border-emerald-200",   bar: "#10B981" },
  "Local Directories": { chip: "bg-[#FEF3E7] text-[#B45309] border-amber-200",     bar: "#C75D33" },
  "Platform":          { chip: "bg-[#FDF2E6] text-[#C75D33] border-[#C75D33]/20",  bar: "#C75D33" },
  "Referral":          { chip: "bg-[#F1F5F9] text-[#475569] border-slate-200",     bar: "#64748B" },
  "Direct":            { chip: "bg-[#F8FAFC] text-[#334155] border-slate-200",     bar: "#94A3B8" },
  "Organic":           { chip: "bg-[#F0FDF4] text-[#166534] border-green-200",     bar: "#22C55E" },
  "Other":             { chip: "bg-[#F1F5F9] text-[#475569] border-slate-200",     bar: "#CBD5E1" },
  "Unknown":           { chip: "bg-[#F1F5F9] text-[#94A3B8] border-slate-200",     bar: "#E2E8F0" },
};

const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtInt = (n) => Number(n || 0).toLocaleString();
const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);

export function SourceChip({ group }) {
  const g = group || "Unknown";
  const c = GROUP_COLORS[g] || GROUP_COLORS.Other;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${c.chip}`}
      data-testid={`source-chip-${String(g).toLowerCase().replace(/\s+/g, "-")}`}
    >
      {g}
    </span>
  );
}

/* Horizontal bar chart — hand-rolled to avoid pulling recharts for a single
 * small viz. Renders bars sized by proportion of total.
 */
function GroupBars({ rows, total, testIdPrefix = "src-row" }) {
  if (!rows || !rows.length) {
    return (
      <div className="rounded-xl bg-slate-50 border border-slate-100 p-6 text-center text-sm text-slate-500">
        No leads captured in this window yet.
      </div>
    );
  }
  return (
    <div className="space-y-2.5">
      {rows.map((r) => {
        const p = total ? Math.max(1, Math.round((r.leads / total) * 100)) : 0;
        const c = GROUP_COLORS[r.group] || GROUP_COLORS.Other;
        return (
          <div key={r.group} data-testid={`${testIdPrefix}-${r.group}`} className="group">
            <div className="flex items-center justify-between gap-3 mb-1">
              <div className="flex items-center gap-2 min-w-0">
                <SourceChip group={r.group} />
                <span className="text-xs text-slate-500 shrink-0">{p}%</span>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <span className="tabular-nums font-semibold text-slate-800">{fmtInt(r.leads)} leads</span>
                {typeof r.quote_total !== "undefined" && (
                  <span className="tabular-nums text-slate-500">{fmtMoney(r.quote_total)} quoted</span>
                )}
              </div>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${p}%`, background: c.bar }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Sources dashboard.
 *
 * mode="company" → GET /api/leads/sources (owner-scoped, shows raw campaigns)
 * mode="admin"   → GET /api/admin/leads/attribution (aggregate, groups only)
 */
export default function Sources({ mode = "company" }) {
  const nav = useNavigate();
  const [days, setDays] = useState(30);
  const [scope, setScope] = useState("all"); // admin only
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const endpoint = useMemo(() => {
    if (mode === "admin") return `/admin/lead-attribution?days=${days}&scope=${scope}`;
    return `/lead-sources?days=${days}`;
  }, [mode, days, scope]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get(endpoint)
      .then(({ data }) => { if (live) setData(data); })
      .catch(() => { if (live) toast.error("Couldn't load sources"); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [endpoint]);

  const title = mode === "admin" ? "Platform lead sources" : "Where your leads come from";
  const subtitle = mode === "admin"
    ? "Aggregate across every tenant + platform funnel. No company data — safe to publish."
    : "Attribution for every quote submitted through your funnel over the selected window.";

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto space-y-6">
        {mode === "admin" && (
          <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900" data-testid="back-to-admin">
            <ArrowLeft className="w-4 h-4" /> Admin
          </Link>
        )}

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="sources-title">
              {title}
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-xl">{subtitle}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="sources-controls">
            {mode === "admin" && (
              <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden text-xs" data-testid="sources-scope">
                {[
                  { key: "all", label: "All" },
                  { key: "tenants", label: "Tenants" },
                  { key: "platform", label: "Platform" },
                ].map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setScope(s.key)}
                    data-testid={`sources-scope-${s.key}`}
                    className={`px-3 py-2 font-medium transition-colors ${scope === s.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
            <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden text-xs" data-testid="sources-range">
              {RANGE_OPTIONS.map((r) => (
                <button
                  key={r.days}
                  onClick={() => setDays(r.days)}
                  data-testid={`sources-range-${r.days}`}
                  className={`px-3 py-2 font-medium transition-colors ${days === r.days ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading || !data ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-16 flex items-center justify-center text-slate-500 text-sm">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-3 gap-4" data-testid="sources-summary">
              <StatCard label="Leads" value={fmtInt(data.summary.total_leads)} sub={`Last ${data.days} days`} />
              <StatCard label="Quoted volume" value={fmtMoney(data.summary.total_quoted)} sub="Sum of all estimates" />
              <StatCard label="Avg quote" value={fmtMoney(data.summary.avg_quote)} sub="Per lead" />
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-display text-lg font-bold text-slate-900">By network</h2>
                  <p className="text-xs text-slate-500 mt-0.5">Normalized to publishable groups (Google, Meta, TikTok, …)</p>
                </div>
                <TrendingUp className="w-5 h-5 text-slate-400" />
              </div>
              <GroupBars rows={data.by_group} total={data.summary.total_leads} testIdPrefix="sources-group-row" />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 className="font-display text-lg font-bold text-slate-900 mb-4">By specific source</h2>
                <div className="divide-y divide-slate-100" data-testid="sources-by-network">
                  {(!data.by_network || !data.by_network.length) && (
                    <div className="text-sm text-slate-500 py-2">Nothing yet.</div>
                  )}
                  {(data.by_network || []).map((r) => (
                    <div key={`${r.network}-${r.group}`} className="flex items-center justify-between py-2 text-sm" data-testid={`sources-network-${r.network}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <SourceChip group={r.group} />
                        <span className="text-slate-700 font-medium truncate">{r.network}</span>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <span className="tabular-nums font-semibold text-slate-900">{fmtInt(r.leads)}</span>
                        {typeof r.quote_total !== "undefined" && (
                          <span className="tabular-nums text-slate-500 hidden sm:inline">{fmtMoney(r.quote_total)}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {mode === "company" && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <h2 className="font-display text-lg font-bold text-slate-900 mb-1">Top campaigns</h2>
                  <p className="text-xs text-slate-500 mb-4">
                    UTM campaigns you're running. Tag your ad URLs with{" "}
                    <code className="px-1 py-0.5 rounded bg-slate-100 text-slate-700 text-[11px]">utm_campaign=spring-sale</code>{" "}
                    to see per-ad performance.
                  </p>
                  <div className="divide-y divide-slate-100" data-testid="sources-top-campaigns">
                    {(!data.top_campaigns || !data.top_campaigns.length) && (
                      <div className="text-sm text-slate-500 py-2">No tagged campaigns yet.</div>
                    )}
                    {(data.top_campaigns || []).map((r, i) => (
                      <div key={i} className="py-2 flex items-center justify-between text-sm" data-testid={`sources-campaign-${i}`}>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 truncate">{r.campaign || "(no campaign)"}</div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {[r.source, r.medium].filter(Boolean).join(" · ") || "—"}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 shrink-0">
                          <span className="tabular-nums font-semibold text-slate-900">{fmtInt(r.leads)}</span>
                          <span className="tabular-nums text-slate-500 hidden sm:inline">{fmtMoney(r.quote_total)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {mode === "admin" && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Filter className="w-4 h-4 text-slate-400" />
                    <h2 className="font-display text-lg font-bold text-slate-900">Publishable stats</h2>
                  </div>
                  <p className="text-xs text-slate-500 mb-4">
                    Share this as-is. No company-identifying data — just channel-level counts you can quote in blog posts or investor updates.
                  </p>
                  <pre className="rounded-lg bg-slate-900 text-slate-100 p-4 text-[11px] font-mono overflow-x-auto" data-testid="sources-publishable-json">
{JSON.stringify({
  window_days: data.days,
  total_leads: data.summary.total_leads,
  total_quoted_usd: data.summary.total_quoted,
  by_network_group: (data.by_group || []).map((r) => ({
    group: r.group,
    leads: r.leads,
    share_pct: pct(r.leads, data.summary.total_leads),
  })),
}, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            {mode === "company" && (
              <div className="rounded-2xl border border-dashed border-slate-200 p-5 text-sm text-slate-600" data-testid="sources-tag-help">
                <div className="flex items-start gap-3">
                  <ExternalLink className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-800 mb-1">Pro tip: tag your ad links</p>
                    <p>
                      Append <code className="px-1 py-0.5 rounded bg-slate-100 text-slate-700 text-[11px]">?utm_source=facebook&amp;utm_campaign=spring-sale</code>{" "}
                      to the URL in your Meta / Google / TikTok ads. Every quote from that ad will show up here credited to that campaign — so you can see which creative and headline actually converts.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{label}</div>
      <div className="text-3xl font-display font-bold text-slate-900 mt-1 tabular-nums">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{sub}</div>
    </div>
  );
}
